/**
 * TikTokCampaignPage — mobile-first, cinematic landing page designed
 * as the destination URL for TikTok bio / link-in-tap / paid CTAs.
 *
 * Renders WITHOUT the app Header / Footer chrome so the experience
 * feels like a full-screen ad the moment a visitor arrives.
 *
 * Route: /tiktok  (public, no auth guard)
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ShieldCheck,
  Sparkles,
  Banknote,
  Wrench,
  Car,
  Building2,
  ArrowLeft,
  BadgeCheck,
  Zap,
  Timer,
} from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_carvo-market/artifacts/c5durw9k_B010DD54-BD83-49AB-A5A3-946EA832B9AF.png";

// Cinematic hero photography — luxury dark-toned automotive.
const HERO_IMAGE =
  "https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=1400&q=90&auto=format&fit=crop";
const CAR_1 =
  "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1200&q=85&auto=format&fit=crop";
const CAR_2 =
  "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=1200&q=85&auto=format&fit=crop";
const CAR_3 =
  "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=1200&q=85&auto=format&fit=crop";

const GOLD = "#c9a55c";
const GOLD_SOFT = "#e8c873";

export default function TikTokCampaignPage() {
  // Track visit for basic analytics (fire-and-forget).
  useEffect(() => {
    try {
      const ref = document.referrer || "";
      window.dispatchEvent(
        new CustomEvent("cartal:tiktok-landing", { detail: { ref } })
      );
    } catch (e) {}
  }, []);

  return (
    <div
      dir="rtl"
      className="min-h-screen bg-[#050505] text-[#f7eecd] font-body overflow-x-hidden"
      data-testid="tiktok-campaign-page"
      style={{
        // A soft cinematic film-grain vibe.
        backgroundImage:
          "radial-gradient(ellipse at top,#0f0b04 0%,#050505 55%,#000 100%)",
      }}
    >
      <HeroSection />
      <ValuePropSection />
      <FeatureGridSection />
      <TrustStripSection />
      <BusinessSection />
      <FinalCTASection />
      <FooterMini />
      {/* Sticky bottom CTA — always visible while scrolling. */}
      <StickyCTA />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   HERO — full-viewport cinematic reveal
   ───────────────────────────────────────────────────────────── */
function HeroSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] flex items-center justify-center overflow-hidden"
      data-testid="tiktok-hero"
    >
      {/* Cinematic parallax background */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0"
        aria-hidden
      >
        <img
          src={HERO_IMAGE}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/20" />
      </motion.div>

      {/* Gold glow */}
      <div
        className="absolute -bottom-20 left-1/2 -translate-x-1/2 w-[140%] h-[300px] blur-[100px] opacity-30 pointer-events-none"
        style={{ background: GOLD }}
        aria-hidden
      />

      <motion.div style={{ opacity }} className="relative z-10 px-6 text-center max-w-2xl">
        <motion.img
          src={LOGO_URL}
          alt="CARTAL"
          className="h-20 sm:h-24 w-auto mx-auto mb-8 drop-shadow-2xl"
          initial={{ opacity: 0, scale: 0.85, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
        />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.9 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 text-[11px] tracking-[0.35em] font-black uppercase"
          style={{
            background: "rgba(201,165,92,0.08)",
            border: `1px solid ${GOLD}55`,
            color: GOLD_SOFT,
          }}
        >
          <Sparkles className="w-3.5 h-3.5" strokeWidth={2.4} />
          THE SMART WAY TO BUY & SELL CARS
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.9 }}
          className="font-display text-[42px] sm:text-6xl md:text-7xl leading-[1.05] font-black tracking-tight"
          style={{ color: "#f7eecd" }}
        >
          <span>سوق السيارات</span>
          <br />
          <span
            style={{
              backgroundImage: `linear-gradient(135deg,${GOLD_SOFT},${GOLD})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            بذكاء كارتال
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.9 }}
          className="mt-5 text-base sm:text-lg text-[#f7eecd]/75 leading-relaxed max-w-md mx-auto"
        >
          كل مركبة، خدمة، وقرار — في مكان واحد.
          <br />
          فحص · تقييم AI · تمويل · تأمين · مخالفات.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-10 flex flex-col items-center gap-3"
        >
          <Link
            to="/join"
            data-testid="tiktok-hero-cta"
            className="group relative inline-flex items-center gap-3 px-10 py-4 rounded-full text-base font-black shadow-2xl transition-transform hover:scale-[1.03] active:scale-[0.98]"
            style={{
              background: `linear-gradient(135deg,${GOLD_SOFT} 0%,${GOLD} 100%)`,
              color: "#0a0a0a",
              boxShadow: `0 20px 50px -10px ${GOLD}80`,
            }}
          >
            <span>انضم إلى كارتال</span>
            <ArrowLeft className="w-5 h-5" strokeWidth={2.6} />
            <span
              className="absolute -inset-1 rounded-full blur-xl opacity-50 -z-10"
              style={{ background: GOLD }}
              aria-hidden
            />
          </Link>
          <div className="text-[11px] tracking-[0.4em] font-bold text-[#f7eecd]/45 uppercase mt-1">
            cartal.sa
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1"
        aria-hidden
      >
        <div className="text-[10px] tracking-[0.35em] text-[#f7eecd]/40 uppercase">
          اسحب للأسفل
        </div>
        <motion.div
          className="w-[1px] h-8"
          style={{ background: `linear-gradient(180deg,${GOLD}, transparent)` }}
          animate={{ scaleY: [0.3, 1, 0.3] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </motion.div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   VALUE PROP — large statement + supporting cinematic image
   ───────────────────────────────────────────────────────────── */
function ValuePropSection() {
  return (
    <section
      className="relative py-24 sm:py-32 px-6 overflow-hidden"
      data-testid="tiktok-value-prop"
    >
      <div className="max-w-3xl mx-auto text-center">
        <FadeUp>
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-6 text-[10px] tracking-[0.35em] font-black uppercase"
            style={{
              background: "rgba(201,165,92,0.06)",
              border: `1px solid ${GOLD}44`,
              color: GOLD_SOFT,
            }}
          >
            <BadgeCheck className="w-3 h-3" strokeWidth={2.6} />
            الأذكى في المملكة
          </div>
        </FadeUp>
        <FadeUp delay={0.15}>
          <h2 className="font-display text-3xl sm:text-5xl font-black leading-[1.15] tracking-tight text-[#f7eecd]">
            ما تشتري سيارة.
            <br />
            <span
              style={{
                backgroundImage: `linear-gradient(135deg,${GOLD_SOFT},${GOLD})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              تختار تجربة كاملة.
            </span>
          </h2>
        </FadeUp>
        <FadeUp delay={0.3}>
          <p className="mt-6 text-base sm:text-lg text-[#f7eecd]/70 leading-relaxed max-w-xl mx-auto">
            كل خطوة موثّقة. كل قرار مدروس. كل صفقة تليق بك — في منصة واحدة
            أنيقة، بلمسة سعودية معاصرة.
          </p>
        </FadeUp>

        <FadeUp delay={0.45}>
          <div className="mt-12 grid grid-cols-3 gap-4 sm:gap-6">
            <StatBlock number="60" unit="دقيقة" label="فحص شامل" />
            <StatBlock number="3.49٪" unit="" label="تمويل يبدأ من" />
            <StatBlock number="AI" unit="" label="تقييم فوري" />
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function StatBlock({ number, unit, label }) {
  return (
    <div
      className="rounded-2xl p-4 sm:p-5 text-center"
      style={{
        background: "rgba(247,238,205,0.03)",
        border: "1px solid rgba(201,165,92,0.15)",
      }}
    >
      <div
        className="font-display text-2xl sm:text-4xl font-black leading-none"
        style={{ color: GOLD_SOFT }}
      >
        {number}
        {unit && (
          <span className="text-xs sm:text-sm font-bold opacity-70 mr-1">
            {" "}
            {unit}
          </span>
        )}
      </div>
      <div className="mt-2 text-[11px] sm:text-xs text-[#f7eecd]/60 tracking-wide">
        {label}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   FEATURE GRID — 6 flagship services with cinematic hover
   ───────────────────────────────────────────────────────────── */
const FEATURES = [
  {
    icon: Car,
    title: "بيع و شراء",
    desc: "سوق موحّد لمركبات موثّقة، بيع سريع وشراء آمن.",
    img: CAR_1,
  },
  {
    icon: ShieldCheck,
    title: "فحص فني شامل",
    desc: "تقرير فحص معتمد قبل الشراء — بشفافية كاملة.",
    img: CAR_2,
  },
  {
    icon: Sparkles,
    title: "تقييم AI ذكي",
    desc: "اعرف القيمة العادلة لمركبتك فوراً بالذكاء الاصطناعي.",
    img: CAR_3,
  },
  {
    icon: Banknote,
    title: "تمويل من 3.49٪",
    desc: "أكثر من 7 بنوك وممولين — عرض مخصص في دقائق.",
    img: CAR_1,
  },
  {
    icon: Zap,
    title: "تأمين + مخالفات",
    desc: "أفضل الأسعار، وتسديد مخالفاتك المرورية بضغطة زر.",
    img: CAR_2,
  },
  {
    icon: Wrench,
    title: "خدمات المركبات",
    desc: "قطع، صيانة، نقل، تخزين، ومقدّمو خدمات موثّقون.",
    img: CAR_3,
  },
];

function FeatureGridSection() {
  return (
    <section
      className="relative py-20 sm:py-28 px-5"
      data-testid="tiktok-features"
    >
      <div className="max-w-4xl mx-auto">
        <FadeUp>
          <h3 className="font-display text-2xl sm:text-4xl font-black text-center mb-12 sm:mb-16 text-[#f7eecd] leading-tight">
            كل ما تحتاجه سيارتك.
            <br />
            <span style={{ color: GOLD_SOFT }}>في تطبيق واحد.</span>
          </h3>
        </FadeUp>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          {FEATURES.map((f, i) => (
            <FeatureCard key={f.title} feature={f} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ feature, index }) {
  const Icon = feature.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.1, ease: "easeOut" }}
      className="group relative overflow-hidden rounded-2xl min-h-[180px] sm:min-h-[210px]"
      style={{
        border: `1px solid ${GOLD}22`,
        background: "linear-gradient(160deg,#0d0902 0%,#050505 100%)",
      }}
    >
      <img
        src={feature.img}
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-35 transition-opacity duration-500"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent" />
      <div className="relative p-5 sm:p-6 h-full flex flex-col">
        <div
          className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center mb-3"
          style={{
            background: `linear-gradient(135deg,${GOLD} 0%,${GOLD_SOFT} 100%)`,
            boxShadow: `0 10px 25px -8px ${GOLD}90`,
          }}
        >
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#0a0a0a]" strokeWidth={2.4} />
        </div>
        <div className="mt-auto">
          <div className="font-display text-lg sm:text-xl font-black text-[#f7eecd]">
            {feature.title}
          </div>
          <div className="text-[13px] sm:text-sm text-[#f7eecd]/70 mt-1 leading-relaxed">
            {feature.desc}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────────────────────
   TRUST STRIP — quick reassurance ticker
   ───────────────────────────────────────────────────────────── */
function TrustStripSection() {
  const items = [
    { icon: ShieldCheck, label: "شركاء موثّقون" },
    { icon: BadgeCheck, label: "مطابق للأنظمة" },
    { icon: Timer, label: "استجابة فورية" },
    { icon: Sparkles, label: "تجربة رؤية 2030" },
  ];
  return (
    <section
      className="py-10 border-y"
      style={{
        borderColor: "rgba(201,165,92,0.15)",
        background: "rgba(201,165,92,0.02)",
      }}
      data-testid="tiktok-trust-strip"
    >
      <div className="max-w-3xl mx-auto px-5 grid grid-cols-2 sm:grid-cols-4 gap-y-6">
        {items.map((it, i) => {
          const I = it.icon;
          return (
            <FadeUp key={it.label} delay={i * 0.08}>
              <div className="flex flex-col items-center gap-2 text-center">
                <I className="w-5 h-5" style={{ color: GOLD_SOFT }} strokeWidth={2.2} />
                <div className="text-[12px] sm:text-sm text-[#f7eecd]/75 font-semibold">
                  {it.label}
                </div>
              </div>
            </FadeUp>
          );
        })}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   CARTAL BUSINESS — dealer/partner appeal
   ───────────────────────────────────────────────────────────── */
function BusinessSection() {
  return (
    <section
      className="relative py-24 sm:py-32 px-5 overflow-hidden"
      data-testid="tiktok-business"
    >
      <div
        className="absolute -top-40 right-1/2 translate-x-1/2 w-[500px] h-[500px] rounded-full blur-3xl opacity-15 pointer-events-none"
        style={{ background: GOLD }}
        aria-hidden
      />
      <div className="max-w-3xl mx-auto text-center relative">
        <FadeUp>
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-6 text-[10px] tracking-[0.35em] font-black uppercase"
            style={{
              background: "rgba(201,165,92,0.08)",
              border: `1px solid ${GOLD}55`,
              color: GOLD_SOFT,
            }}
          >
            <Building2 className="w-3.5 h-3.5" strokeWidth={2.6} />
            CARTAL BUSINESS
          </div>
        </FadeUp>
        <FadeUp delay={0.1}>
          <h3 className="font-display text-3xl sm:text-5xl font-black leading-tight text-[#f7eecd]">
            للتجّار، المعارض،
            <br />
            <span
              style={{
                backgroundImage: `linear-gradient(135deg,${GOLD_SOFT},${GOLD})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              ومقدّمي الخدمات
            </span>
          </h3>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="mt-5 text-[15px] sm:text-lg text-[#f7eecd]/70 leading-relaxed max-w-xl mx-auto">
            مساحة عمل موحّدة بلوحة تحكم مخصّصة — إعلانات، موظفين، تحليلات،
            وشارة توثيق ذهبية أمام آلاف العملاء.
          </p>
        </FadeUp>
        <FadeUp delay={0.32}>
          <Link
            to="/join?intent=partner"
            data-testid="tiktok-business-cta"
            className="mt-10 inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full text-[15px] font-black transition-all hover:scale-[1.03]"
            style={{
              background: "rgba(247,238,205,0.06)",
              color: "#f7eecd",
              border: `1.5px solid ${GOLD}`,
              boxShadow: `0 15px 40px -15px ${GOLD}`,
            }}
          >
            <Building2 className="w-4 h-4" strokeWidth={2.4} />
            <span>سجّل معرضك أو خدمتك</span>
            <ArrowLeft className="w-4 h-4" strokeWidth={2.4} />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   FINAL CTA — big closing shot
   ───────────────────────────────────────────────────────────── */
function FinalCTASection() {
  return (
    <section
      className="relative py-24 sm:py-32 px-6 overflow-hidden"
      data-testid="tiktok-final-cta"
    >
      <img
        src={HERO_IMAGE}
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-30"
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/70 to-[#050505]" />

      <div className="relative max-w-2xl mx-auto text-center">
        <FadeUp>
          <img
            src={LOGO_URL}
            alt="CARTAL"
            className="h-16 sm:h-20 w-auto mx-auto mb-6"
          />
        </FadeUp>
        <FadeUp delay={0.15}>
          <h3 className="font-display text-3xl sm:text-5xl font-black leading-tight text-[#f7eecd]">
            جاهز تنطلق
            <br />
            <span
              style={{
                backgroundImage: `linear-gradient(135deg,${GOLD_SOFT},${GOLD})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              مع كارتال؟
            </span>
          </h3>
        </FadeUp>
        <FadeUp delay={0.3}>
          <p className="mt-5 text-[15px] sm:text-lg text-[#f7eecd]/70 leading-relaxed">
            سجّل مجاناً في أقل من دقيقة. اختر نوع حسابك، وابدأ رحلتك.
          </p>
        </FadeUp>
        <FadeUp delay={0.45}>
          <Link
            to="/join"
            data-testid="tiktok-final-cta"
            className="mt-10 inline-flex items-center gap-3 px-12 py-4 rounded-full text-lg font-black shadow-2xl transition-transform hover:scale-[1.03]"
            style={{
              background: `linear-gradient(135deg,${GOLD_SOFT} 0%,${GOLD} 100%)`,
              color: "#0a0a0a",
              boxShadow: `0 20px 50px -8px ${GOLD}`,
            }}
          >
            <span>انضم إلى كارتال</span>
            <ArrowLeft className="w-5 h-5" strokeWidth={2.6} />
          </Link>
        </FadeUp>
        <FadeUp delay={0.6}>
          <div className="mt-6 text-[11px] tracking-[0.4em] font-bold text-[#f7eecd]/45 uppercase">
            cartal.sa
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   FOOTER MINI — one-liner
   ───────────────────────────────────────────────────────────── */
function FooterMini() {
  return (
    <footer className="text-center py-8 px-5 text-[11px] text-[#f7eecd]/40 pb-28">
      © {new Date().getFullYear()} CARTAL — كل مركبة تستحق منصة تليق بها.
    </footer>
  );
}

/* ─────────────────────────────────────────────────────────────
   STICKY CTA — always-visible bottom bar
   ───────────────────────────────────────────────────────────── */
function StickyCTA() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const on = () => setVisible(window.scrollY > 320);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 pt-3 transition-transform duration-300 ${
        visible ? "translate-y-0" : "translate-y-full"
      }`}
      style={{
        background: "linear-gradient(180deg,rgba(5,5,5,0) 0%,rgba(5,5,5,0.95) 40%,rgba(5,5,5,1) 100%)",
        paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
      }}
      aria-hidden={!visible}
    >
      <Link
        to="/join"
        data-testid="tiktok-sticky-cta"
        className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full text-base font-black shadow-2xl"
        style={{
          background: `linear-gradient(135deg,${GOLD_SOFT} 0%,${GOLD} 100%)`,
          color: "#0a0a0a",
          boxShadow: `0 20px 50px -8px ${GOLD}`,
        }}
      >
        <span>انضم إلى كارتال</span>
        <ArrowLeft className="w-5 h-5" strokeWidth={2.6} />
      </Link>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Helper — Fade-up scroll reveal
   ───────────────────────────────────────────────────────────── */
function FadeUp({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}
